import {
  ShopNotificationCatalogEntry,
  ShopNotificationRule,
  ShopNotificationSettings,
} from '../@models/shop-notification-settings';

export const SHOP_NOTIFICATION_CATALOG = [
  {
    type: 'appointment.created',
    labelKey: 'notifications.settings.appointmentCreated.title',
    descriptionKey: 'notifications.settings.appointmentCreated.description',
    mandatory: false,
    defaultRecipients: ['owner', 'admin', 'assigned_member'],
    allowedRecipients: ['owner', 'admin', 'manager', 'assigned_member'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'appointment.pending_confirmation',
    labelKey: 'notifications.settings.appointmentPendingConfirmation.title',
    descriptionKey: 'notifications.settings.appointmentPendingConfirmation.description',
    mandatory: false,
    defaultRecipients: ['owner', 'admin', 'assigned_member'],
    allowedRecipients: ['owner', 'admin', 'manager', 'assigned_member'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'appointment.cancelled',
    labelKey: 'notifications.settings.appointmentCancelled.title',
    descriptionKey: 'notifications.settings.appointmentCancelled.description',
    mandatory: false,
    defaultRecipients: ['owner', 'admin', 'assigned_member'],
    allowedRecipients: ['owner', 'admin', 'manager', 'assigned_member'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'appointment.confirmed',
    labelKey: 'notifications.settings.appointmentConfirmed.title',
    descriptionKey: 'notifications.settings.appointmentConfirmed.description',
    mandatory: false,
    defaultRecipients: ['assigned_member'],
    allowedRecipients: ['owner', 'admin', 'manager', 'assigned_member'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'appointment.rescheduled',
    labelKey: 'notifications.settings.appointmentRescheduled.title',
    descriptionKey: 'notifications.settings.appointmentRescheduled.description',
    mandatory: false,
    defaultRecipients: ['assigned_member'],
    allowedRecipients: ['owner', 'admin', 'manager', 'assigned_member'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'appointment.no_show',
    labelKey: 'notifications.settings.appointmentNoShow.title',
    descriptionKey: 'notifications.settings.appointmentNoShow.description',
    mandatory: false,
    defaultRecipients: ['owner', 'manager'],
    allowedRecipients: ['owner', 'admin', 'manager', 'assigned_member'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'member.invitation_accepted',
    labelKey: 'notifications.settings.memberInvitationAccepted.title',
    descriptionKey: 'notifications.settings.memberInvitationAccepted.description',
    mandatory: false,
    defaultRecipients: ['owner', 'admin'],
    allowedRecipients: ['owner', 'admin'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'member.suspended',
    labelKey: 'notifications.settings.memberSuspended.title',
    descriptionKey: 'notifications.settings.memberSuspended.description',
    mandatory: false,
    defaultRecipients: ['owner'],
    allowedRecipients: ['owner', 'admin'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'review.created',
    labelKey: 'notifications.settings.reviewCreated.title',
    descriptionKey: 'notifications.settings.reviewCreated.description',
    mandatory: false,
    defaultRecipients: ['owner', 'admin'],
    allowedRecipients: ['owner', 'admin', 'manager'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'payment.failed',
    labelKey: 'notifications.settings.paymentFailed.title',
    descriptionKey: 'notifications.settings.paymentFailed.description',
    mandatory: true,
    defaultRecipients: ['owner', 'permission:shop.billing.manage'],
    allowedRecipients: ['owner', 'permission:shop.billing.manage'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'subscription.payment_failed',
    labelKey: 'notifications.settings.subscriptionPaymentFailed.title',
    descriptionKey: 'notifications.settings.subscriptionPaymentFailed.description',
    mandatory: true,
    defaultRecipients: ['owner', 'permission:shop.billing.manage'],
    allowedRecipients: ['owner', 'permission:shop.billing.manage'],
    allowedChannels: ['in_app', 'email'],
  },
  {
    type: 'shop.closure_appointment_conflict',
    labelKey: 'notifications.settings.closureConflict.title',
    descriptionKey: 'notifications.settings.closureConflict.description',
    mandatory: false,
    defaultRecipients: ['owner', 'admin', 'manager'],
    allowedRecipients: ['owner', 'admin', 'manager'],
    allowedChannels: ['in_app', 'email'],
  },
] as const satisfies readonly ShopNotificationCatalogEntry[];

export const SHOP_NOTIFICATION_CATALOG_BY_TYPE = new Map<string, ShopNotificationCatalogEntry>(
  SHOP_NOTIFICATION_CATALOG.map((entry) => [entry.type, entry]),
);

export function defaultShopNotificationRules(): Record<string, ShopNotificationRule> {
  return Object.fromEntries(
    SHOP_NOTIFICATION_CATALOG.map((entry) => [
      entry.type,
      {
        enabled: true,
        recipients: [...entry.defaultRecipients],
        channels: [...entry.allowedChannels],
      },
    ]),
  );
}

export function normalizeShopNotificationSettings(
  shopId: string,
  value?: Partial<ShopNotificationSettings>,
): ShopNotificationSettings {
  const defaults = defaultShopNotificationRules();
  const rules = Object.fromEntries(
    SHOP_NOTIFICATION_CATALOG.map((entry) => {
      const current = value?.rules?.[entry.type];
      return [
        entry.type,
        {
          ...defaults[entry.type],
          ...current,
          enabled: entry.mandatory ? true : (current?.enabled ?? true),
          recipients: current?.recipients ?? defaults[entry.type].recipients,
          channels: current?.channels ?? defaults[entry.type].channels,
        },
      ];
    }),
  );
  return { schemaVersion: 1, shopId, rules };
}
